import React from 'react';
import './rush.css';
import './features.css';
import './feature-detail.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { FI, FeatHero, FeatCapabilities, FeatDetailRow, FeatSteps, FeatQuote, FeatCrossLinks, FeatFinalCTA } from './RushFeatureLayout';

const BVHeroVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Brand voice · German</span></div>
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {[['Formality', 72, 'Sie · formal'], ['Warmth', 60, 'Warm'], ['Playfulness', 28, 'Serious'], ['Energy', 48, 'Even']].map(([l, v, tag]) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12.5 }}>
            <span style={{ width: 86, color: 'var(--ink-3)' }}>{l}</span>
            <span style={{ flex: 1, height: 6, background: 'var(--bg-soft)', borderRadius: 999, border: '1px solid var(--line)' }}>
              <span style={{ display: 'block', height: '100%', width: v + '%', background: 'var(--accent)', borderRadius: 999 }}></span>
            </span>
            <span style={{ width: 64, fontSize: 11, color: 'var(--ink-4)', textAlign: 'right' }}>{tag}</span>
          </div>
        ))}
      </div>
      <div style={{ border: '1px solid var(--line)', borderRadius: 11, padding: 13, background: 'var(--bg-soft)' }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', color: 'var(--ink-4)', textTransform: 'uppercase' }}>Generic engine</div>
        <div style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 5, lineHeight: 1.5 }}>Willkommen zurück! Bereit, dort weiterzumachen, wo Sie aufgehört haben?</div>
      </div>
      <div style={{ border: '1px solid color-mix(in oklab, var(--accent) 38%, var(--line))', borderRadius: 11, padding: 13, background: 'color-mix(in oklab, var(--accent) 6%, var(--bg-card))' }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', color: 'var(--accent)', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 6 }}>{FI.ai} On-brand · Rush AI</div>
        <div style={{ fontSize: 14.5, color: 'var(--ink)', fontWeight: 500, marginTop: 6, lineHeight: 1.5 }}>Schön, dass Sie wieder da sind. Möchten Sie dort weitermachen, wo Sie aufgehört haben?</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 11, flexWrap: 'wrap' }}>
          {['Sie · formal', 'Warm tone', 'German profile'].map((c) => (
            <span key={c} style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--accent-2)', background: 'var(--accent-soft)', padding: '3px 9px', borderRadius: 999 }}>{c}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const BVRulesVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Terminology rules</span></div>
    <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', color: 'var(--ink-4)', textTransform: 'uppercase', marginBottom: 9 }}>Do not translate</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {['Rush Studio', 'Acme', 'Rush AI', 'Translation Memory'].map((x) => (
            <span key={x} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, color: 'var(--ink-2)', background: 'var(--bg-soft)', border: '1px solid var(--line)', padding: '4px 10px', borderRadius: 999 }}>
              <span style={{ color: 'var(--ink-4)' }}>{FI.lock}</span>{x}
            </span>
          ))}
        </div>
      </div>
      <div style={{ border: '1px solid var(--line)', borderLeft: '3px solid #d2604f', borderRadius: 9, padding: '11px 13px', background: 'var(--bg-soft)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>"cheap"</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Off-brand — use "cost-effective". We're premium.</div>
      </div>
      <div style={{ border: '1px solid var(--line)', borderLeft: '3px solid var(--green)', borderRadius: 9, padding: '11px 13px', background: 'var(--bg-soft)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>sign in <span style={{ color: 'var(--ink-4)', margin: '0 4px' }}>→</span> <span style={{ color: 'var(--green)' }}>anmelden</span></div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>Preferred over "einloggen" across every project.</div>
      </div>
    </div>
  </div>
);

const RushFeatureBrandVoice = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="features" />
      <FeatHero
        tint=""
        kicker="Brand voice"
        kickerIcon={FI.voice}
        title="Your brand, in every language."
        lead="Most tools treat brand voice as a flat term list. Rush Studio makes it a living profile — tone, formality, and forbidden phrasings the AI actually follows — and lets you preview the voice on real copy before a single word ships."
        primary={{ label: 'Get early access', to: '/register' }}
        secondary={{ label: 'See all features', to: '/features' }}
        meta={['Per-language profiles', 'Live voice preview', 'Glossary-aware']}
        visual={<BVHeroVisual />}
      />

      <FeatCapabilities
        tint=""
        eyebrow="What it does"
        title="A voice profile that trains the AI."
        lead="Four controls that turn a style guide nobody reads into rules the model enforces on every draft."
        caps={[
          { icon: FI.sliders, title: 'Tone & form of address', desc: 'Dial in formality, warmth, playfulness, and energy. Pick Sie/du, vous/tu per language.' },
          { icon: FI.lock, title: 'Do-not-translate & forbidden', desc: 'Lock product names verbatim; ban off-brand wording with the reason attached.' },
          { icon: FI.ai, title: 'Try the voice, live', desc: 'See generic machine translation vs your on-brand output side by side, instantly.' },
          { icon: FI.layers, title: 'Per-language profiles', desc: "Train the model with on-brand example pairs — show, don't tell." },
        ]}
      />

      <FeatDetailRow
        reverse
        eyebrow="Terminology that holds"
        title="Lock the words that matter."
        intro="Brand names stay verbatim. Off-brand words are banned with the reason attached, so reviewers stop relitigating the same edits. Preferred phrasings are reached for first — consistently, across every language and every project."
        points={[
          { icon: FI.lock, title: 'Do-not-translate list', desc: 'Product and brand names pass through untouched in all 100+ languages.' },
          { icon: FI.search, title: 'Forbidden & preferred terms', desc: '"cheap" → "cost-effective". The AI follows it; reviewers don\'t have to.' },
          { icon: FI.layers, title: 'Example-pair training', desc: 'A few on-brand pairs teach the model your style faster than any rulebook.' },
        ]}
        replaces={['Style guides buried in Google Docs', 'Endless reviewer back-and-forth']}
        visual={<BVRulesVisual />}
      />

      <FeatSteps
        eyebrow="How it works"
        title="From style guide to enforced voice."
        steps={[
          { h: 'Set the profile', p: 'Move the tone sliders, pick the form of address, and paste your do-not-translate and forbidden lists.' },
          { h: 'Preview on real copy', p: 'Test the voice against live sample strings — compare generic MT to your on-brand output.' },
          { h: 'Ship it everywhere', p: 'The profile applies to every draft, in every language, for every project that uses it.' },
        ]}
      />

      <FeatQuote quote="We stopped having the same five edit fights on every project. The voice profile just enforces them now." initials="MR" name="Mira Roth" role="Localization lead, Northwind" />

      <FeatCrossLinks current="brand-voice" />

      <FeatFinalCTA title="Lock your voice in every language." sub="Join the waitlist for early-access pricing and a 1:1 onboarding call with our team." />

      <RushFooter />
    </div>
  );
};

export default RushFeatureBrandVoice;
